import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useParams } from "react-router";
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import CardCommon from "../../../../components/card/CardCommon";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import DialogCommon from "../../../../components/dialogs/DialogCommon";
import TextfieldDefault from "../../../../components/textField/TextfieldDefaultNew";
import { CustomTheme } from "../../../../types/customTheme";
import {
  createLocationPass,
  fetchLicenseDetails,
  fetchSingleLicenseDetails,
  getLocationPasses,
} from "../../../../services/locationApp/licenseService";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";

interface License {
  licenseCode: string;
  name: string;
  deviceType: string;
  status: string;
}

interface Pass {
  passId: string;
  comments: string;
  created: string;
  createdBy: string;
  deviceType?: string;
  expiresAt: string;
  licenseCode: string;
  locationId: string;
  name?: string;
}

interface LicensePassGroup {
  licenseCode: string;
  passes: Pass[];
}

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    tableContainer: {
      backgroundColor: theme.palette.background.default,
      boxShadow: "none",
    },
    tableRow: {
      "& td": {
        borderBottom: "none",
        padding: "2px 4px",
        fontSize: "12px",
        color: theme.palette.text.primary,
        border: "none",
      },
    },
    cardWrapperCell: {
      padding: 4,
      borderBottom: "none",
      border: "none",
    },
    table: {
      backgroundColor: "transparent",
      borderCollapse: "separate",
      borderSpacing: 0,
      border: "none",
    },
    rowText: {
      wordBreak: "break-word",
    },
    licensePickerContainer: {
      marginTop: 8,
      maxHeight: 220,
      overflowY: "auto",
      borderRadius: 10,
      backgroundColor: theme.palette.background.entity_dialog_textfield_background,
    },
    licensePickerTable: {
      tableLayout: "fixed",
    },
    licensePickerHeaderCell: {
      padding: "8px",
      fontSize: "12px",
      fontWeight: 700,
      color: theme.palette.text.primary,
      borderBottom: "none",
    },
    licensePickerCell: {
      padding: "8px",
      fontSize: "12px",
      color: theme.palette.text.primary,
      borderBottom: "none",
      wordBreak: "break-word",
    },
    licensePickerRow: {
      cursor: "pointer",
      "&:hover td": {
        backgroundColor: theme.palette.background.entity_background,
      },
    },
    selectedLicensePickerRow: {
      "& td": {
        backgroundColor: theme.palette.background.entity_background,
        fontWeight: 700,
      },
    },
    licensePickerEmpty: {
      padding: "14px 8px",
      textAlign: "center",
      color: theme.palette.text.secondary,
    },
    licensePickerError: {
      color: theme.palette.custom.red.main,
      fontSize: 12,
      marginTop: 8,
    },
  }),
);

const formatDateTime = (date?: string) => {
  if (!date) return "-";

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return date;

  return `${parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })} - ${parsedDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;
};

const getPassCreatedTime = (pass: Pass) => {
  const createdTime = new Date(pass.created).getTime();
  return Number.isNaN(createdTime) ? 0 : createdTime;
};

const hasActivePassForLicense = (
  passGroups: LicensePassGroup[],
  licenseCode: string,
) => {
  const now = Date.now();

  return passGroups.some((group) => {
    if (group.licenseCode !== licenseCode) return false;

    return (group.passes || []).some((pass) => {
      const expiresAt = new Date(pass.expiresAt || "").getTime();
      return !Number.isNaN(expiresAt) && expiresAt > now;
    });
  });
};

const getCreatedPassFromResponse = (
  response: any,
  licenseCode: string,
  locationId: string,
  comments: string,
): Pass => {
  const payload = response?.data?.data;
  const responsePass =
    payload?.pass ||
    payload?.createdPass ||
    payload?.locationPass ||
    (payload?.passId ? payload : null) ||
    (Array.isArray(payload?.passes)
      ? payload.passes[payload.passes.length - 1]
      : null) ||
    (Array.isArray(payload) ? payload[payload.length - 1] : null) ||
    {};

  return {
    passId: responsePass.passId || responsePass.id || `${licenseCode}-${Date.now()}`,
    comments: responsePass.comments || comments,
    created: responsePass.created || new Date().toISOString(),
    createdBy: responsePass.createdBy || responsePass.created_by || "-",
    expiresAt:
      responsePass.expiresAt ||
      responsePass.expires_at ||
      new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    licenseCode: responsePass.licenseCode || payload?.licenseCode || licenseCode,
    locationId: responsePass.locationId || payload?.locationId || locationId,
  };
};

const PassLicense: React.FC = () => {
  const classes = useStyles();
  const { locationId } = useParams<{ locationId: string }>();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [passGroups, setPassGroups] = useState<LicensePassGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedLicenseCode, setSelectedLicenseCode] = useState("");
  const [selectedLicenseDetails, setSelectedLicenseDetails] = useState<any>(null);
  const [selectedLicenseError, setSelectedLicenseError] = useState("");
  const [checkingLicenseCode, setCheckingLicenseCode] = useState("");
  const [licenseSearchText, setLicenseSearchText] = useState("");
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState("");

  const getToken = () => Cookies.get("idToken") || null;

  const loadPassPageData = async () => {
    if (!locationId) return;

    setLoading(true);
    setError("");

    try {
      const token = getToken();
      if (!token) throw new Error("Missing Firebase token");

      const [licenseResponse, passesResponse] = await Promise.all([
        fetchLicenseDetails(locationId, token),
        getLocationPasses(locationId, token),
      ]);

      setLicenses(licenseResponse?.data?.data || []);
      setPassGroups(passesResponse?.data?.data || []);
    } catch (err) {
      console.error("Passes page load error", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setLicenses([]);
      setPassGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    setSelectedLicenseCode("");
    setSelectedLicenseDetails(null);
    setSelectedLicenseError("");
    setCheckingLicenseCode("");
    setLicenseSearchText("");
    setComments("");
    setCommentsError("");
  };

  const handleLicenseSelect = async (licenseCode: string) => {
    if (!locationId) return;

    setSelectedLicenseCode("");
    setSelectedLicenseDetails(null);
    setSelectedLicenseError("");
    setCommentsError("");
    setCheckingLicenseCode(licenseCode);

    try {
      const response = await fetchSingleLicenseDetails(locationId, licenseCode);
      const details = response?.data?.data || {};
      const status = String(details.status || "").toLowerCase();

      if (status === "active") {
        setSelectedLicenseDetails(details);
        setSelectedLicenseCode(licenseCode);
        return;
      }

      if (status === "locked") {
        const responseDetails = {
          ...details,
          status: hasActivePassForLicense(passGroups, licenseCode)
            ? "active"
            : details.status,
        };

        setSelectedLicenseDetails(responseDetails);
        setSelectedLicenseCode(licenseCode);
        return;
      }

      setSelectedLicenseError(
        "Selected license must be active or locked to create a pass.",
      );
    } catch (err) {
      console.error("GET selected license details error", err);
      setSelectedLicenseError("Unable to validate selected license.");
    } finally {
      setCheckingLicenseCode("");
    }
  };

  const handleCreatePass = async () => {
    if (!locationId || !selectedLicenseCode) return;

    if (!selectedLicenseDetails) {
      setSelectedLicenseError("Please select a valid license.");
      return;
    }

    if (!comments.trim()) {
      setCommentsError("Comments are required.");
      return;
    }

    const token = getToken();
    if (!token) {
      setCommentsError("Missing Firebase token.");
      return;
    }

    const passComments = comments.trim();
    const passLicenseCode = selectedLicenseCode;

    const response = await createLocationPass(
      locationId,
      {
        licenseCode: passLicenseCode,
        comments: passComments,
      },
      token,
    );

    const createdPass = getCreatedPassFromResponse(
      response,
      passLicenseCode,
      locationId,
      passComments,
    );

    setPassGroups((currentGroups) => {
      const existingGroup = currentGroups.find(
        (group) => group.licenseCode === createdPass.licenseCode,
      );

      if (!existingGroup) {
        return [
          {
            licenseCode: createdPass.licenseCode,
            passes: [createdPass],
          },
          ...currentGroups,
        ];
      }

      return currentGroups.map((group) =>
        group.licenseCode === createdPass.licenseCode
          ? {
              ...group,
              passes: [
                createdPass,
                ...(group.passes || []).filter(
                  (pass) => pass.passId !== createdPass.passId,
                ),
              ],
            }
          : group,
      );
    });

    handleCloseCreateModal();
  };

  useEffect(() => {
    document.title = "Passes";
    loadPassPageData();
  }, [locationId]);

  const passRows = passGroups.reduce<Pass[]>((rows, group) => {
    (group.passes || []).forEach((pass) => {
      const passLicenseCode = pass.licenseCode || group.licenseCode;
      const license = licenses.find(
        (licenseItem) => licenseItem.licenseCode === passLicenseCode,
      );

      rows.push({
        ...pass,
        deviceType: license?.deviceType,
        licenseCode: passLicenseCode,
        name: license?.name,
      });
    });

    return rows;
  }, []).sort((firstPass, secondPass) => (
    getPassCreatedTime(secondPass) - getPassCreatedTime(firstPass)
  ));

  const normalizedLicenseSearchText = licenseSearchText.trim().toLowerCase();
  const filteredLicenses = licenses.filter((license) => {
    if (!normalizedLicenseSearchText) return true;

    return [license.name, license.licenseCode, license.deviceType].some((value) =>
      String(value || "").toLowerCase().includes(normalizedLicenseSearchText),
    );
  });

  return (
    <>
      <Box px={3} pt={3} pb={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" align="left">
            License Passes
          </Typography>

          <ButtonCommon
            color="green"
            onClick={() => setOpenCreateModal(true)}
            isDisabled={loading || licenses.length === 0}
          >
            Create Pass
          </ButtonCommon>
        </Box>
      </Box>

      <Box px={3}>
        {loading ? (
          <CardCommon
            backgroundColor="entity_background"
            style={{ marginTop: 16, border: "none", boxShadow: "none" }}
          >
            <TableContainer className={classes.tableContainer}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={7} className={classes.cardWrapperCell}>
                      <CardCommon
                        backgroundColor="table_header_background"
                        style={{ borderRadius: 10, marginBottom: 0 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          flexWrap="wrap"
                          px={1.5}
                          height="42px"
                          alignItems="center"
                        >
                          <Box width="14%" />
                          <Box width="16%" />
                          <Box width="14%" />
                          <Box width="16%" />
                          <Box width="15%" />
                          <Box width="12%" />
                          <Box width="13%" />
                        </Box>
                      </CardCommon>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {[...Array(4)].map((_, index) => (
                    <TableRow key={index} className={classes.tableRow}>
                      <TableCell colSpan={7} className={classes.cardWrapperCell}>
                        <CardCommon
                          backgroundColor="entity_background"
                          style={{ borderRadius: 10, marginBottom: 1 }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            px={1.5}
                            minHeight="42px"
                            alignItems="center"
                          >
                            <Box width="14%" />
                            <Box width="16%" />
                            <Box width="14%" />
                            <Box width="16%" />
                            <Box width="15%" />
                            <Box width="12%" />
                            <Box width="13%" />
                          </Box>
                        </CardCommon>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardCommon>
        ) : error ? (
          <Typography color="error" align="center">
            {error}
          </Typography>
        ) : passRows.length === 0 ? (
          <Typography color="textSecondary" align="center">
            No passes found.
          </Typography>
        ) : (
          <CardCommon
            backgroundColor="entity_background"
            style={{ marginTop: 16, border: "none", boxShadow: "none" }}
          >
            <TableContainer className={classes.tableContainer}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={7} className={classes.cardWrapperCell}>
                      <CardCommon
                        backgroundColor="table_header_background"
                        style={{ borderRadius: 10, marginBottom: 0 }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          flexWrap="wrap"
                          px={1.5}
                          height="42px"
                          alignItems="center"
                          textAlign="left"
                        >
                          <Box width="14%">
                            <strong>License Code</strong>
                          </Box>
                          <Box width="16%">
                            <strong>Name</strong>
                          </Box>
                          <Box width="14%">
                            <strong>Device Type</strong>
                          </Box>
                          <Box width="16%">
                            <strong>Comments</strong>
                          </Box>
                          <Box width="15%">
                            <strong>Created</strong>
                          </Box>
                          <Box width="12%">
                            <strong>Created By</strong>
                          </Box>
                          <Box width="13%">
                            <strong>Expires At</strong>
                          </Box>
                        </Box>
                      </CardCommon>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {passRows.map((pass) => (
                    <TableRow
                      key={pass.passId || `${pass.licenseCode}-${pass.created}`}
                      className={classes.tableRow}
                    >
                      <TableCell colSpan={7} className={classes.cardWrapperCell}>
                        <CardCommon
                          backgroundColor="entity_background"
                          style={{ borderRadius: 10, marginBottom: 1 }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            px={1.5}
                            minHeight="42px"
                            alignItems="center"
                            textAlign="left"
                          >
                            <Box width="14%" className={classes.rowText}>
                              {pass.licenseCode || "-"}
                            </Box>
                            <Box width="16%" className={classes.rowText}>
                              {pass.name || "-"}
                            </Box>
                            <Box width="14%" className={classes.rowText}>
                              {pass.deviceType || "-"}
                            </Box>
                            <Box width="16%" className={classes.rowText}>
                              {pass.comments || "-"}
                            </Box>
                            <Box width="15%" className={classes.rowText}>
                              {formatDateTime(pass.created)}
                            </Box>
                            <Box width="12%" className={classes.rowText}>
                              {pass.createdBy || "-"}
                            </Box>
                            <Box width="13%" className={classes.rowText}>
                              {formatDateTime(pass.expiresAt)}
                            </Box>
                          </Box>
                        </CardCommon>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardCommon>
        )}
      </Box>

      <DialogCommon
        open={openCreateModal}
        setOpen={(isOpen: boolean) => {
          if (!isOpen) handleCloseCreateModal();
          setOpenCreateModal(isOpen);
        }}
      >
        <Box width={{ xs: "auto", sm: 560 }}>
          <DialogTitle>Create Pass</DialogTitle>
          <DialogContent>
            <TextfieldDefault
              id="pass-license-search"
              name="pass-license-search"
              label="Search License"
              value={licenseSearchText}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setLicenseSearchText(event.target.value);
              }}
              type="text"
              placeholder="Search by name, license code or device type"
            />

            <TableContainer className={classes.licensePickerContainer}>
              <Table size="small" stickyHeader className={classes.licensePickerTable}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      width="34%"
                      className={classes.licensePickerHeaderCell}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      width="36%"
                      className={classes.licensePickerHeaderCell}
                    >
                      License Code
                    </TableCell>
                    <TableCell
                      width="30%"
                      className={classes.licensePickerHeaderCell}
                    >
                      Device Type
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLicenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className={classes.licensePickerCell}>
                        <Box className={classes.licensePickerEmpty}>
                          No licenses found.
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLicenses.map((license) => (
                      <TableRow
                        key={license.licenseCode}
                        hover
                        selected={selectedLicenseCode === license.licenseCode}
                        onClick={() => handleLicenseSelect(license.licenseCode)}
                        className={`${classes.licensePickerRow} ${
                          selectedLicenseCode === license.licenseCode
                            ? classes.selectedLicensePickerRow
                            : ""
                        }`}
                      >
                        <TableCell className={classes.licensePickerCell}>
                          {license.name || "-"}
                        </TableCell>
                        <TableCell className={classes.licensePickerCell}>
                          {checkingLicenseCode === license.licenseCode
                            ? "Checking..."
                            : license.licenseCode || "-"}
                        </TableCell>
                        <TableCell className={classes.licensePickerCell}>
                          {license.deviceType || "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {selectedLicenseError && (
              <Typography className={classes.licensePickerError}>
                {selectedLicenseError}
              </Typography>
            )}

            <Box mt={2}>
              <TextfieldDefault
                id="pass-comments"
                name="pass-comments"
                label="Comments"
                value={comments}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setComments(event.target.value);
                  setCommentsError("");
                }}
                disabled={!selectedLicenseCode || Boolean(checkingLicenseCode)}
                type="text"
                multiline
                rows={4}
                error={Boolean(commentsError)}
                helperText={commentsError}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <ButtonCommon color="yellow" onClick={handleCloseCreateModal}>
              Cancel
            </ButtonCommon>
            <ButtonCommon
              color="green"
              onClick={handleCreatePass}
              isDisabled={!selectedLicenseCode || Boolean(checkingLicenseCode)}
              style={{ minWidth: 110 }}
            >
              Create Pass
            </ButtonCommon>
          </DialogActions>
        </Box>
      </DialogCommon>
    </>
  );
};

export default PassLicense;
